import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { DateRange } from "react-day-picker";
import { DateRangeSelector } from "@/components/vacation/DateRangeSelector";
import { BusyPeriodsList } from "./BusyPeriodsList";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { COUNTRIES, countryCodeToFlag } from "@/utils/countryUtils";

export function BusyPeriodManager() {
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [description, setDescription] = useState("");
  const [teamScope, setTeamScope] = useState<"sales" | "production">("production");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [country, setCountry] = useState<string>("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: busyPeriods, isLoading } = useQuery({
    queryKey: ["busy_periods"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("busy_periods")
        .select("*")
        .order("start_date", { ascending: true });
      
      if (error) throw error;
      return data;
    }
  });

  const handleMarkBusy = async () => {
    if (isSubmitting) return;
    if (!dateRange?.from || !dateRange?.to) {
      toast({
        title: "Please select both start and end dates",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "You must be logged in to mark busy periods",
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }

      const { error } = await supabase.from("busy_periods").insert({
        start_date: dateRange.from.toISOString().split("T")[0],
        end_date: dateRange.to.toISOString().split("T")[0],
        description,
        created_by: user.id,
        team_scope: teamScope,
        country: country || null
      });

      if (error) throw error;

      toast({
        title: "Busy period marked successfully",
      });

      setDateRange(undefined);
      setDescription("");
      setCountry("");
      queryClient.invalidateQueries({ queryKey: ["busy_periods"] });
    } catch (error) {
      console.error("Error in handleMarkBusy:", error);
      toast({
        title: "Error marking busy period",
        description: "Make sure you have manager permissions",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from("busy_periods")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Busy period deleted successfully",
      });

      queryClient.invalidateQueries({ queryKey: ["busy_periods"] });
    } catch (error) {
      console.error("Error deleting busy period:", error);
      toast({
        title: "Error deleting busy period",
        description: "Make sure you have manager permissions",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <Card className="hover:shadow-lg transition-shadow duration-300">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4 space-y-0">
        <div>
          <CardTitle>
            Manage Events
            <span className="block text-sm text-muted-foreground font-normal">
              Set and manage company-wide events
            </span>
          </CardTitle>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <div className="min-w-[150px]">
            <Select value={teamScope} onValueChange={(value: "sales" | "production") => setTeamScope(value)}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select team scope" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="production">Production</SelectItem>
                <SelectItem value="sales">Sales</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="min-w-[160px]">
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Country" />
              </SelectTrigger>
              <SelectContent>
                {COUNTRIES.map(c => (
                  <SelectItem key={c.code} value={c.code}>
                    {countryCodeToFlag(c.code)} {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="min-w-[200px]">
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Description"
            />
          </div>

          <Button
            onClick={handleMarkBusy}
            disabled={isSubmitting}
            className="transition-all hover:scale-[1.02]"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Mark as Busy"
            )}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <DateRangeSelector
          selectedDate={dateRange}
          setSelectedDate={setDateRange}
          busyPeriods={busyPeriods}
          fullWidth
        />

        <div className="flex items-center gap-4 flex-wrap border rounded-lg px-4 py-3 bg-muted/30">
          <div className="flex items-center gap-1.5 text-sm">
            <div className="w-3 h-3 rounded-full bg-destructive"></div>
            <span className="text-muted-foreground">Production Busy</span>
          </div>
          <div className="flex items-center gap-1.5 text-sm">
            <div className="w-3 h-3 rounded-full bg-[#9b87f5]"></div>
            <span className="text-muted-foreground">Sales Busy</span>
          </div>
          <div className="flex items-center gap-1.5 text-sm">
            <div className="w-3 h-3 rounded-full bg-muted"></div>
            <span className="text-muted-foreground">Weekend</span>
          </div>
          <div className="flex items-center gap-1.5 text-sm">
            <div className="w-3 h-3 rounded-full bg-primary"></div>
            <span className="text-muted-foreground">Selected</span>
          </div>
          <div className="flex items-center gap-1.5 text-sm">
            <div className="w-3 h-3 rounded-full bg-accent"></div>
            <span className="text-muted-foreground">Today</span>
          </div>
        </div>

      </CardContent>
    </Card>
  );
}
